import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Preloader, Text, useResponsive } from '@common-fe/common-fe';
import styled, { css } from 'styled-components';

import { TBD_TITLE } from '@/common/constants';

import { useInvestmentsHoldings } from '../../hooks';
import { useGetInvestorAccountId, useInvestmentsProcessingSnapshot } from '../hook';

import { getSplittedMoneyAmount } from './utils';

interface TitleProps {
  isMobileMode?: boolean;
  value: number;
}

const StyledTitle = styled.h3<TitleProps>`
  font-family: 'Oswald', serif;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textOnColor};
  ${({ value, isMobileMode }) => {
    if (value > 999999) {
      if (isMobileMode) {
        return css`
          font-size: 72px;
          line-height: 72px;
        `;
      }
      return css`
        font-size: 98px;
        line-height: 98px;
      `;
    }
    if (value > 99999) {
      if (isMobileMode) {
        return css`
          font-size: 86px;
          line-height: 86px;
        `;
      }
      return css`
        font-size: 118px;
        line-height: 118px;
      `;
    }
    if (value > 9999) {
      if (isMobileMode) {
        return css`
          font-size: 96px;
          line-height: 96px;
        `;
      }
      return css`
        font-size: 138px;

        line-height: 138px;
      `;
    }
    if (isMobileMode) {
      return css`
        font-size: 100px;
        line-height: 100px;
      `;
    }
    return css`
      font-size: 150px;
      line-height: 150px;
    `;
  }}
`;

const StyledSubtext = styled.span`
  font-family: 'Oswald', serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.textOnColor};
`;

interface Props {}

const InvestmentBannerTitle: React.FC<Props> = () => {
  const { t } = useTranslation();
  const accountId = useGetInvestorAccountId();
  const { isMobile } = useResponsive();
  const {
    details: { portfolioBalance = 0 },
    isHoldingLoading,
  } = useInvestmentsHoldings(accountId);
  const { value: mainAmount, decimal } = useMemo(
    () => getSplittedMoneyAmount(portfolioBalance),
    [portfolioBalance]
  );
  const isSnapshotShowed = useInvestmentsProcessingSnapshot();
  return (
    <Box direction="column" data-testid="investment-banner">
      <Text color="textOnColor" style={{ opacity: 0.5 }}>
        {t('Investment portfolio balance')}
      </Text>
      {isHoldingLoading ? (
        <Box height="180px" width={{ min: '200px' }} align="center" justify="center">
          <Preloader size={50} />
        </Box>
      ) : isSnapshotShowed ? (
        <Box direction="row" fill align="end">
          <StyledTitle data-testid="investment-banner_value" value={portfolioBalance}>
            {TBD_TITLE}
          </StyledTitle>
        </Box>
      ) : (
        <Box direction="row" fill align="end">
          <StyledTitle data-testid="investment-banner_value" isMobileMode={isMobile} value={portfolioBalance}>
            {mainAmount}
          </StyledTitle>
          <StyledSubtext data-testid="investment-banner_decimals" color="textOnColor">
            {decimal}
          </StyledSubtext>
        </Box>
      )}
      {isSnapshotShowed && (
        <Box
          data-testid="investment-banner_snapshot-text"
          direction="column"
          margin={{ top: 'spacing24', right: 'spacing48' }}
        >
          <Text color="investmentsProfit" size="medium" weight={700}>
            {t("We're processing your investment")}
          </Text>
          <Text color="investmentsProfit" size="medium" margin={{ top: 'spacing2' }}>
            {t('Investment purchases can take 1-3 business days to settle and be reflected in your account')}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default InvestmentBannerTitle;
